import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@saas-boilerplate/shared';

interface TrialStatus {
  /** Days remaining in the 30-day trial (can be negative if expired) */
  daysRemaining: number;
  /** True if user is within the 30-day trial window and has no active subscription or exemption */
  isTrialActive: boolean;
  /** True if 30-day window has passed and no active subscription or exemption exists */
  isTrialExpired: boolean;
  /** True if any workspace the user belongs to has an active Stripe subscription */
  hasActiveSubscription: boolean;
  /** True if any workspace the user belongs to is on a company flagged billing_exempt - no payment required */
  isBillingExempt: boolean;
  /** Still fetching trial info */
  isLoading: boolean;
}

const TRIAL_DAYS = 30;

function daysSince(dateString: string): number {
  const created = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export function useTrialStatus(): TrialStatus {
  const [isLoading, setIsLoading] = useState(true);
  const [earliestCreatedAt, setEarliestCreatedAt] = useState<string | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isBillingExempt, setIsBillingExempt] = useState(false);

  const fetchTrialInfo = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_user_trial_info');
      if (error) {
        console.error('Failed to fetch trial info:', error);
        return;
      }
      if (data) {
        setEarliestCreatedAt(data.earliest_workspace_created_at ?? null);
        setHasActiveSubscription(data.has_any_active_subscription ?? false);
        setIsBillingExempt(data.is_billing_exempt ?? false);
      }
    } catch (err) {
      console.error('Error fetching trial info:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrialInfo();
  }, [fetchTrialInfo]);

  if (isLoading || !earliestCreatedAt) {
    return {
      daysRemaining: TRIAL_DAYS,
      isTrialActive: false,
      isTrialExpired: false,
      hasActiveSubscription,
      isBillingExempt,
      isLoading,
    };
  }

  const elapsed = daysSince(earliestCreatedAt);
  const daysRemaining = TRIAL_DAYS - elapsed;
  // Either an active Stripe subscription OR the billing_exempt flag bypasses the expiry gate.
  const accessGranted = hasActiveSubscription || isBillingExempt;

  return {
    daysRemaining,
    isTrialActive: daysRemaining > 0 && !accessGranted,
    isTrialExpired: daysRemaining <= 0 && !accessGranted,
    hasActiveSubscription,
    isBillingExempt,
    isLoading,
  };
}
